import { User, GlobalConfig, RankTierConfig, AccountTier } from '../types';

// Default rank configurations - aligned with centralized config v2.0
const DEFAULT_RANKS: RankTierConfig[] = [
    {
        id: 'standard',
        name: 'Standard',
        displayName: 'Standard',
        description: 'Free tier with basic access',
        monthlyPrice: 0,
        yearlyPrice: 0,
        price: 0,
        tokenLimit: 5000,           // ~100 messages/day
        tokenRefreshRate: 800,      // 800 tokens/hour
        refreshIntervalMinutes: 60,
        dailyAttachmentLimit: 3,
        maxFileSizeMB: 5,
        maxFileSize: 5,
        maxSessionHistory: 50,
        defaultModel: 'gemini-2.5-flash-lite',
        aiModel: 'gemini-2.5-flash-lite',
        advancedModelsAccess: false,
        priorityQueue: false,
        features: ['5,000 tokens/day', 'Basic AI models', '3 attachments/day'],
        color: '#64748b',
        gradientFrom: '#94a3b8',
        gradientTo: '#64748b',
        iconEmoji: '👤',
        colors: {
            primary: '#64748b',
            secondary: '#94a3b8',
            gradientFrom: '#94a3b8',
            gradientTo: '#64748b',
            text: '#64748b',
            background: '#f8fafc',
            chatBubble: '#f1f5f9',
            chatBubbleText: '#334155',
            avatarBorder: '#cbd5e1',
            glowColor: 'rgba(100, 116, 139, 0.5)'
        },
        badge: {
            text: '',
            icon: '👤',
            showInHeader: false,
            showInSidebar: false
        },
        isActive: true,
        displayOrder: 1,
        showInUpgradeModal: false
    },
    {
        id: 'premium',
        name: 'Premium',
        displayName: 'Premium',
        description: 'Enhanced features and higher limits',
        monthlyPrice: 299,
        yearlyPrice: 2499,
        price: 299,
        tokenLimit: 50000,          // ~1000 messages/day
        tokenRefreshRate: 5000,     // 5K tokens/hour
        refreshIntervalMinutes: 60,
        dailyAttachmentLimit: -1,
        maxFileSizeMB: 50,
        maxFileSize: 50,
        maxSessionHistory: -1,
        defaultModel: 'gemini-2.5-flash-lite',
        aiModel: 'gemini-2.5-flash-lite',
        advancedModelsAccess: true,
        priorityQueue: true,
        features: ['50,000 tokens/day', 'Unlimited attachments', 'Priority support'],
        color: '#f59e0b',
        gradientFrom: '#fbbf24',
        gradientTo: '#f59e0b',
        iconEmoji: '👑',
        colors: {
            primary: '#f59e0b',
            secondary: '#fbbf24',
            gradientFrom: '#fbbf24',
            gradientTo: '#f59e0b',
            text: '#f59e0b',
            background: '#fffbeb',
            chatBubble: '#fef3c7',
            chatBubbleText: '#92400e',
            avatarBorder: '#fcd34d',
            glowColor: 'rgba(245, 158, 11, 0.5)'
        },
        badge: {
            text: 'POPULAR',
            icon: '👑',
            showInHeader: true,
            showInSidebar: true
        },
        isActive: true,
        displayOrder: 2,
        showInUpgradeModal: true
    },
    {
        id: 'max',
        name: 'MAX',
        displayName: 'MAX',
        description: 'Ultimate power user experience',
        monthlyPrice: 999,
        yearlyPrice: 7999,
        price: 999,
        tokenLimit: 200000,         // ~4000 messages/day
        tokenRefreshRate: 20000,    // 20K tokens/hour
        refreshIntervalMinutes: 60,
        dailyAttachmentLimit: -1,
        maxFileSizeMB: 100,
        maxFileSize: 100,
        maxSessionHistory: -1,
        defaultModel: 'gemini-2.5-flash-lite',
        aiModel: 'gemini-2.5-flash-lite',
        advancedModelsAccess: true,
        priorityQueue: true,
        features: ['200,000 tokens/day', 'Unlimited everything', 'VIP support'],
        color: '#ec4899',
        gradientFrom: '#a855f7',
        gradientTo: '#ec4899',
        iconEmoji: '💎',
        colors: {
            primary: '#ec4899',
            secondary: '#a855f7',
            gradientFrom: '#a855f7',
            gradientTo: '#ec4899',
            text: '#ec4899',
            background: '#fdf2f8',
            chatBubble: '#fce7f3',
            chatBubbleText: '#831843',
            avatarBorder: '#f9a8d4',
            glowColor: 'rgba(236, 72, 153, 0.5)'
        },
        badge: {
            text: '',
            icon: '💎',
            showInHeader: true,
            showInSidebar: true
        },
        isActive: true,
        displayOrder: 3,
        showInUpgradeModal: true
    }
];

/**
 * Determine user's actual tier based on subscription status
 * Priority: accountTier > explicit MAX flag > Premium flag > Standard
 */
export function determineUserTier(user: User): AccountTier {
    // First check explicit accountTier field (most reliable)
    if (user.accountTier) {
        return user.accountTier;
    }

    // Then check explicit MAX flag - but ONLY if not also Premium
    // This handles the case where isMax was incorrectly set
    if (user.isMax === true || user.isMaxTier === true) {
        return 'max';
    }

    // Then check Premium status
    if (user.isPremium) {
        return 'premium';
    }

    // Default to standard
    return 'standard';
}

/**
 * Get the user's current rank/tier information
 */
export function getUserRank(user: User, config: GlobalConfig): RankTierConfig {
    // Use the reliable tier determination function
    const tierId = determineUserTier(user);

    // 1. Try to find in global config first (if available)
    if (config.rankTiers && Array.isArray(config.rankTiers)) {
        const rank = config.rankTiers.find(r => r.id === tierId);
        if (rank) return rank;
    }

    // 2. Fallback to default ranks if not found in config
    const defaultRank = DEFAULT_RANKS.find(r => r.id === tierId);

    // 3. Ultimate fallback to standard
    return defaultRank || DEFAULT_RANKS[0];
}

/**
 * Check if user is MAX tier
 */
export function isUserMaxTier(user: User): boolean {
    return determineUserTier(user) === 'max';
}

/**
 * Check if user is Premium tier (NOT MAX)
 */
export function isUserPremiumTier(user: User): boolean {
    return determineUserTier(user) === 'premium';
}

/**
 * Check if user is Standard tier
 */
export function isUserStandardTier(user: User): boolean {
    return determineUserTier(user) === 'standard';
}

/**
 * Get rank color (returns hex color)
 */
export function getRankColor(user: User, config: GlobalConfig): string {
    const rank = getUserRank(user, config);
    // If config has rank but missing colors, fallback to default rank for that tier
    if (rank.colors?.primary) return rank.colors.primary;

    const defaultRank = DEFAULT_RANKS.find(r => r.id === rank.id);
    return defaultRank?.colors?.primary || DEFAULT_RANKS[0].colors.primary;
}

/**
 * Get rank gradient colors
 */
export function getRankGradient(user: User, config: GlobalConfig): { from: string; to: string } {
    const rank = getUserRank(user, config);
    const colors = rank.colors;

    if (colors?.gradientFrom && colors?.gradientTo) {
        return {
            from: colors.gradientFrom,
            to: colors.gradientTo
        };
    }

    // Fallback to default rank for that tier
    const defaultRank = DEFAULT_RANKS.find(r => r.id === rank.id);
    const defaultColors = defaultRank?.colors || DEFAULT_RANKS[0].colors;

    return {
        from: defaultColors.gradientFrom || defaultColors.primary,
        to: defaultColors.gradientTo || defaultColors.primary
    };
}

/**
 * Get rank display name
 */
export function getRankName(user: User, config: GlobalConfig): string {
    const rank = getUserRank(user, config);
    return rank.name;
}

/**
 * Get rank badge text (emoji or text)
 */
export function getRankBadge(user: User, config: GlobalConfig): string {
    const rank = getUserRank(user, config);
    return rank.badge?.text || '';
}
